/**
 * MyGroupRequestsList Component
 * Displays user's own group requests separated into active and historical sections
 *
 * Features:
 * - Separate tables for active (pending) and historical (approved/rejected/cancelled) requests
 * - Shows basic info: group name, status, requested date
 * - Shows processing info: who approved/rejected, when, rejection reason
 * - Read-only view (no actions)
 * - Loading and empty states for each section
 */

import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  PendingActions as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Block as CancelledIcon,
  Error as FailedIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useMyGroupRequests } from '../../hooks/api/useGroupRequests';
import { useUsers } from '../../hooks/api/useUsers';
import { formatDistanceToNow } from '../../utils/formatting/dateUtils';
import { GroupRequest, RequestStatus } from '../../api/data-contracts';
import { GroupRequestDetailsDialog } from './GroupRequestDetailsDialog';

interface MyGroupRequestsListProps {
  /**
   * Optional callback when component needs to refresh data
   */
  onDataChange?: () => void;
}

interface RequestRowProps {
  request: GroupRequest;
  showProcessingInfo: boolean;
  showDetailsButton: boolean;
  getUserDisplayName: (userId: string) => string;
  getStatusIcon: (status: RequestStatus) => React.ReactElement;
  getStatusColor: (status: RequestStatus) => 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';
  onViewDetails?: (request: GroupRequest) => void;
  t: (key: string) => string;
}

/**
 * Individual request row component
 * Separated to properly use React hooks for expand/collapse state
 */
const RequestRow: React.FC<RequestRowProps> = ({
  request,
  showProcessingInfo,
  showDetailsButton,
  getUserDisplayName,
  getStatusIcon,
  getStatusColor,
  onViewDetails,
  t,
}) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <React.Fragment key={request.id}>
      <TableRow hover>
        {/* Group Name */}
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {request.group_name || t('groupRequests.approval.unknownGroup')}
          </Typography>
          {request.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {request.description}
            </Typography>
          )}
        </TableCell>

        {/* Status */}
        <TableCell>
          <Chip
            icon={getStatusIcon(request.status || RequestStatus.Pending)}
            label={t(`groupRequests.status.${request.status || 'pending'}`)}
            color={getStatusColor(request.status || RequestStatus.Pending)}
            size="small"
          />
        </TableCell>

        {/* Requested Date */}
        <TableCell>
          <Typography variant="body2">
            {request.requested_at
              ? formatDistanceToNow(new Date(request.requested_at), {
                  addSuffix: true,
                })
              : '-'}
          </Typography>
        </TableCell>

        {/* Details Button (for active requests) */}
        {showDetailsButton && (
          <TableCell align="center" sx={{ width: 48 }}>
            <IconButton
              size="small"
              onClick={() => onViewDetails?.(request)}
              aria-label={t('groupRequests.myRequests.detailDialog.viewDetails')}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </TableCell>
        )}

        {/* Processing Info (for historical requests) */}
        {showProcessingInfo && (
          <>
            <TableCell>
              <Typography variant="body2">
                {request.processed_by
                  ? getUserDisplayName(request.processed_by)
                  : '-'}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">
                {request.processed_at
                  ? formatDistanceToNow(new Date(request.processed_at), {
                      addSuffix: true,
                    })
                  : '-'}
              </Typography>
            </TableCell>
            <TableCell>
              {request.rejection_reason && (
                <IconButton
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s',
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              )}
            </TableCell>
          </>
        )}
      </TableRow>

      {/* Expandable rejection reason row */}
      {showProcessingInfo && request.rejection_reason && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded ? undefined : 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 2, px: 2, backgroundColor: 'error.50' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {t('groupRequests.details.rejectionReason')}:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {request.rejection_reason}
                </Typography>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

export const MyGroupRequestsList: React.FC<MyGroupRequestsListProps> = ({
  onDataChange,
}) => {
  const { t } = useTranslation();

  // State for detail dialog
  const [selectedRequest, setSelectedRequest] = React.useState<GroupRequest | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = React.useState(false);

  // Fetch user's own requests
  const {
    data: requestsResponse,
    isLoading: requestsLoading,
    error: requestsError,
  } = useMyGroupRequests({ page: 1, pageSize: 100 });

  // Fetch users for displaying approver/processor names
  const { data: usersData } = useUsers({ skip: 0, limit: 1000 });
  const allUsers = usersData?.users || [];

  const requests = requestsResponse?.requests || [];

  // Create user lookup map for efficient access
  const userMap = useMemo(() => {
    const map = new Map();
    allUsers.forEach((user) => {
      map.set(user.id, user);
    });
    return map;
  }, [allUsers]);

  // Helper to get user display name
  const getUserDisplayName = (userId: string): string => {
    const user = userMap.get(userId);
    if (!user) {return userId;}
    return user.full_name || user.username || user.email || userId;
  };

  // Separate requests into active and historical
  const { activeRequests, historicalRequests } = useMemo(() => {
    const active: GroupRequest[] = [];
    const historical: GroupRequest[] = [];

    requests.forEach((request) => {
      if (request.status === RequestStatus.Pending) {
        active.push(request);
      } else {
        historical.push(request);
      }
    });

    return { activeRequests: active, historicalRequests: historical };
  }, [requests]);

  // Helper to get status color
  const getStatusColor = (
    status: RequestStatus
  ): 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' => {
    switch (status) {
      case RequestStatus.Pending:
        return 'warning';
      case RequestStatus.Approved:
        return 'success';
      case RequestStatus.Rejected:
        return 'error';
      case RequestStatus.Cancelled:
        return 'info';
      case RequestStatus.Failed:
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper to get status icon
  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.Pending:
        return <PendingIcon fontSize="small" />;
      case RequestStatus.Approved:
        return <ApprovedIcon fontSize="small" />;
      case RequestStatus.Rejected:
        return <RejectedIcon fontSize="small" />;
      case RequestStatus.Cancelled:
        return <CancelledIcon fontSize="small" />;
      case RequestStatus.Failed:
        return <FailedIcon fontSize="small" />;
      default:
        return <PendingIcon fontSize="small" />;
    }
  };

  // Handler to open detail dialog
  const handleViewDetails = (request: GroupRequest) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  // Handler to close detail dialog
  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedRequest(null);
  };

  // Show loading state
  if (requestsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (requestsError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {requestsError instanceof Error
          ? requestsError.message
          : t('groupRequests.approval.errorLoading')}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Active Requests Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('groupRequests.myRequests.activeRequests')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('groupRequests.myRequests.activeRequestsDescription')}
        </Typography>

        {activeRequests.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
            <PendingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('groupRequests.myRequests.noActiveRequests')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('groupRequests.details.groupName')}</TableCell>
                  <TableCell>{t('groupRequests.details.status')}</TableCell>
                  <TableCell>{t('groupRequests.details.requestedAt')}</TableCell>
                  <TableCell align="center" sx={{ width: 48 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeRequests.map((request) => (
                  <RequestRow
                    key={request.id}
                    request={request}
                    showProcessingInfo={false}
                    showDetailsButton={true}
                    getUserDisplayName={getUserDisplayName}
                    getStatusIcon={getStatusIcon}
                    getStatusColor={getStatusColor}
                    onViewDetails={handleViewDetails}
                    t={t}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Historical Requests Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('groupRequests.myRequests.requestHistory')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('groupRequests.myRequests.requestHistoryDescription')}
        </Typography>

        {historicalRequests.length === 0 ? (
          <Paper variant="outlined" sx={{ py: 6, textAlign: 'center' }}>
            <ApprovedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              {t('groupRequests.myRequests.noRequestHistory')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('groupRequests.details.groupName')}</TableCell>
                  <TableCell>{t('groupRequests.details.status')}</TableCell>
                  <TableCell>{t('groupRequests.details.requestedAt')}</TableCell>
                  <TableCell>{t('groupRequests.details.processedBy')}</TableCell>
                  <TableCell>{t('groupRequests.details.processedAt')}</TableCell>
                  <TableCell sx={{ width: 48 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historicalRequests.map((request) => (
                  <RequestRow
                    key={request.id}
                    request={request}
                    showProcessingInfo={true}
                    showDetailsButton={false}
                    getUserDisplayName={getUserDisplayName}
                    getStatusIcon={getStatusIcon}
                    getStatusColor={getStatusColor}
                    t={t}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Detail Dialog */}
      <GroupRequestDetailsDialog
        open={detailDialogOpen}
        request={selectedRequest}
        onClose={handleCloseDetailDialog}
      />
    </Box>
  );
};
